import { useCallback, useEffect, useRef, useState } from "react";

export type Role = "admin" | "leader";
export type TabRole = "input" | "report";
export type ColumnType = "text" | "number" | "date" | "time" | "select" | "formula";
export type Granularity = "daily" | "weekly" | "monthly";

/** A single sheet cell. `undefined` covers keys the upstream may omit. */
export type RowValue = string | number | boolean | null | undefined;

export interface SchemaColumn {
  key: string;
  label: string;
  type: ColumnType;
  required: boolean;
  readOnly?: boolean;
  options?: string[];
}

export interface SchemaTab {
  name: string;
  label: string;
  role: TabRole;
  columns: SchemaColumn[];
}

export interface SchemaResponse {
  tabs: SchemaTab[];
}

export interface Me {
  email: string;
  role: Role;
  name: string;
}

export interface RowsResponse {
  columns: SchemaColumn[];
  rows: Array<Record<string, RowValue>>;
}

export interface PostRowResponse {
  row: Record<string, RowValue>;
}

export interface ReportBucket {
  key: string;
  label: string;
  value: number;
  groups?: Record<string, number>;
}

export interface ReportMeta {
  tab: string;
  metric: string;
  dim: string;
}

export interface ReportResponse {
  granularity: Granularity;
  total: number;
  meta: ReportMeta;
  buckets: ReportBucket[];
}

export interface RowsQuery {
  tab: string;
  limit?: number;
  from?: string;
  to?: string;
}

export interface ReportQuery {
  tab: string;
  granularity: Granularity;
  from?: string;
  to?: string;
  metric: string;
  dim: string;
}

/** Typed error carrying the envelope's `code` plus the HTTP status. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

type Envelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } };

function isEnvelope(value: unknown): value is Envelope<unknown> {
  return typeof value === "object" && value !== null && "ok" in value;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(path, {
      ...init,
      headers: {
        Accept: "application/json",
        ...(init?.body ? { "Content-Type": "application/json" } : {}),
        ...init?.headers,
      },
    });
  } catch (error) {
    throw new ApiError(
      "network_error",
      "Tidak dapat menghubungi server. Periksa koneksi Anda lalu coba lagi.",
      0,
    );
  }

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (isEnvelope(payload)) {
    if (payload.ok) {
      return payload.data as T;
    }
    throw new ApiError(
      payload.error?.code ?? "unknown_error",
      payload.error?.message ?? "Terjadi kesalahan pada server.",
      response.status,
    );
  }

  throw new ApiError(
    "invalid_response",
    `Respons server tidak dapat dibaca (HTTP ${response.status}).`,
    response.status,
  );
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }
  if (error instanceof Error) {
    return new ApiError("unknown_error", error.message, 0);
  }
  return new ApiError("unknown_error", "Terjadi kesalahan yang tidak diketahui.", 0);
}

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
}

function useAsync<T>(
  factory: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[],
  enabled: boolean,
): AsyncState<T> {
  const factoryRef = useRef(factory);
  factoryRef.current = factory;

  const [tick, setTick] = useState(0);
  const [state, setState] = useState<{
    data: T | null;
    loading: boolean;
    error: ApiError | null;
  }>({ data: null, loading: enabled, error: null });

  useEffect(() => {
    if (!enabled) {
      setState({ data: null, loading: false, error: null });
      return;
    }

    const controller = new AbortController();
    let active = true;

    setState({ data: null, loading: true, error: null });

    factoryRef.current(controller.signal).then(
      (data) => {
        if (active) {
          setState({ data, loading: false, error: null });
        }
      },
      (error: unknown) => {
        if (!active || controller.signal.aborted) {
          return;
        }
        setState({ data: null, loading: false, error: toApiError(error) });
      },
    );

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, tick]);

  const reload = useCallback(() => {
    setTick((value) => value + 1);
  }, []);

  return { data: state.data, loading: state.loading, error: state.error, reload };
}

export function useMe(): AsyncState<Me> {
  return useAsync((signal) => request<Me>("/api/me", { signal }), [], true);
}

export function useSchema(): AsyncState<SchemaResponse> {
  return useAsync((signal) => request<SchemaResponse>("/api/schema", { signal }), [], true);
}

export function useRows(query: RowsQuery | null): AsyncState<RowsResponse> {
  const key = query ? JSON.stringify(query) : "";
  return useAsync(
    (signal) => {
      if (!query) {
        return Promise.reject(
          new ApiError("missing_tab", "Tab belum dipilih.", 400),
        );
      }
      const params = new URLSearchParams({ tab: query.tab });
      if (query.limit !== undefined) {
        params.set("limit", String(query.limit));
      }
      if (query.from) {
        params.set("from", query.from);
      }
      if (query.to) {
        params.set("to", query.to);
      }
      return request<RowsResponse>(`/api/rows?${params.toString()}`, { signal });
    },
    [key],
    query !== null,
  );
}

export function useReport(query: ReportQuery | null): AsyncState<ReportResponse> {
  const key = query ? JSON.stringify(query) : "";
  return useAsync(
    (signal) => {
      if (!query) {
        return Promise.reject(
          new ApiError("missing_tab", "Tab belum dipilih.", 400),
        );
      }
      const params = new URLSearchParams({
        tab: query.tab,
        granularity: query.granularity,
        metric: query.metric,
        dim: query.dim,
      });
      if (query.from) {
        params.set("from", query.from);
      }
      if (query.to) {
        params.set("to", query.to);
      }
      return request<ReportResponse>(`/api/report?${params.toString()}`, { signal });
    },
    [key],
    query !== null,
  );
}

export function postRow(
  tab: string,
  values: Record<string, string | number>,
): Promise<PostRowResponse> {
  return request<PostRowResponse>("/api/rows", {
    method: "POST",
    body: JSON.stringify({ tab, values }),
  });
}
